import { Component, EventEmitter, OnInit, OnDestroy, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClientService, Client, Todo } from '../../services/client.service';
import { TenantService } from '../../services/tenant.service';
import { UserService } from '../../services/user.service';
import { NotificationService } from '../../services/notification.service';
import { Subscription } from 'rxjs';

/**
 * Standalone "create task" modal that can be opened from anywhere (e.g. the global
 * "New" shortcut) without navigating. Mirrors the create path used on the Tasks page.
 */
@Component({
  selector: 'app-add-task-modal',
  imports: [CommonModule, FormsModule],
  templateUrl: './add-task-modal.html',
})
export class AddTaskModalComponent implements OnInit, OnDestroy {
  private clientService = inject(ClientService);
  private tenantService = inject(TenantService);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);

  @Output() close = new EventEmitter<void>();
  @Output() created = new EventEmitter<void>();

  tenants: { id: string; name: string }[] = [];
  clients: Client[] = [];
  users: any[] = [];
  filteredClients: Client[] = [];
  filteredProjects: any[] = [];
  selectedUsers: string[] = [];
  private currentUser: any = null;
  private subs: Subscription[] = [];

  form: Partial<Todo> & { name: string } = { name: '', description: '', status: 'TODO', priority: 'MEDIUM' };
  scope: { tenantId?: string; clientId?: string; projectId?: string } = {};
  hasDueDate = false;
  dueDate = '';
  saving = false;

  async ngOnInit() {
    await Promise.all([this.clientService.init(), this.tenantService.init(), this.userService.init()]);
    this.subs.push(
      this.tenantService.tenants$.subscribe(t => (this.tenants = t)),
      this.clientService.clients$.subscribe(c => (this.clients = c)),
      this.userService.users$.subscribe(u => (this.users = u)),
      this.userService.currentUser$.subscribe(u => {
        this.currentUser = u;
        // Default-assign the task to the current user.
        if (u?.id && this.selectedUsers.length === 0) this.selectedUsers = [u.id];
      }),
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  onTenantChange() {
    this.scope.clientId = undefined;
    this.scope.projectId = undefined;
    this.filteredProjects = [];
    this.filteredClients = this.scope.tenantId
      ? this.clients.filter(c => (c as any).tenantId === this.scope.tenantId)
      : [];
  }

  async onClientChange() {
    this.scope.projectId = undefined;
    this.filteredProjects = this.scope.clientId
      ? await this.clientService.getProjectsByClient(this.scope.clientId)
      : [];
  }

  isUserSelected(id: string): boolean {
    return this.selectedUsers.includes(id);
  }

  toggleUser(id: string) {
    this.selectedUsers = this.isUserSelected(id)
      ? this.selectedUsers.filter(u => u !== id)
      : [...this.selectedUsers, id];
  }

  async save() {
    const name = this.form.name?.trim();
    if (!name || this.saving) return;
    this.saving = true;
    try {
      const newTodo = await this.clientService.createTodo({
        name,
        description: this.form.description || undefined,
        status: this.form.status || 'TODO',
        priority: this.form.priority || 'MEDIUM',
        dueDate: this.hasDueDate && this.dueDate ? this.dueDate : undefined,
        tenantId: this.scope.tenantId,
        clientId: this.scope.clientId,
        projectId: this.scope.projectId,
        creatorId: this.currentUser?.id ?? undefined,
      } as Partial<Todo>);

      if (newTodo) {
        for (const uid of this.selectedUsers) {
          await this.clientService.linkTodoToUser(newTodo.id, uid);
        }
        const me = this.currentUser;
        if (me?.id) {
          const senderName = [me.firstName, me.name].filter(Boolean).join(' ') || 'A colleague';
          this.notificationService
            .notifyTodoAssignment({
              todoName: name,
              newUserIds: this.selectedUsers,
              previousUserIds: [],
              currentUserId: me.id,
              senderName,
              todoId: newTodo.id,
            })
            .catch((e: any) => console.error('[AddTask] Notification error:', e));
        }
      }
      this.created.emit();
      this.close.emit();
    } catch (e) {
      console.error('[AddTask] Failed to create task', e);
    } finally {
      this.saving = false;
    }
  }
}
